/**
 * Subcontractor Profile Page
 * Allows TG to view and edit their own profile
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import { usePageTracking } from '../../hooks/usePageTracking'
import toast from 'react-hot-toast'
import { CommentSection } from '../../components/comments/CommentSection'
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  Save,
  Loader2,
  ChevronUp,
  ChevronDown,
  AlertCircle
} from 'lucide-react'

interface ProfileData {
  id: string
  name: string
  email: string
  phone: string | null
  company: string | null
  org_number: string | null
  address_street: string | null
  address_postal_code: string | null
  address_city: string | null
  address_country: string | null
  available_days: boolean[]
  color: string | null
  is_admin: boolean
  created_at: string
}

interface FormData {
  phone: string
  company: string
  org_number: string
  address_street: string
  address_postal_code: string
  address_city: string
  address_country: string
  available_days: boolean[]
}

const DAY_NAMES = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön']

export function MyProfile() {
  usePageTracking('profile')

  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [showEmailChange, setShowEmailChange] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [sectionsOpen, setSectionsOpen] = useState({
    profile: true,
    availability: true,
    contact: true,
    comments: false
  })

  const [formData, setFormData] = useState<FormData>({
    phone: '',
    company: '',
    org_number: '',
    address_street: '',
    address_postal_code: '',
    address_city: '',
    address_country: 'Sverige',
    available_days: [true, true, true, true, true, false, false]
  })

  const [originalData, setOriginalData] = useState<FormData | null>(null)

  // Fetch profile
  const { data: profile, isLoading } = useQuery<ProfileData>({
    queryKey: ['my-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('Not authenticated')
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .eq('user_id', user.id)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!user?.id
  })

  // Update form when profile loads
  useEffect(() => {
    if (profile) {
      const data: FormData = {
        phone: profile.phone || '',
        company: profile.company || '',
        org_number: profile.org_number || '',
        address_street: profile.address_street || '',
        address_postal_code: profile.address_postal_code || '',
        address_city: profile.address_city || '',
        address_country: profile.address_country || 'Sverige',
        available_days: profile.available_days || [true, true, true, true, true, false, false]
      }
      setFormData(data)
      setOriginalData(data)
    }
  }, [profile])

  // Check for unsaved changes
  const hasChanges = originalData && JSON.stringify(formData) !== JSON.stringify(originalData)

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const { data: result, error } = await supabase.rpc('update_my_profile', {
        p_phone: data.phone || null,
        p_company: data.company || null,
        p_org_number: data.org_number || null,
        p_address_street: data.address_street || null,
        p_address_postal_code: data.address_postal_code || null,
        p_address_city: data.address_city || null,
        p_address_country: data.address_country || null,
        p_available_days: data.available_days
      })
      if (error) throw error
      return result
    },
    onSuccess: () => {
      toast.success('Profilen har sparats')
      setOriginalData(formData)
      setIsEditing(false)
      queryClient.invalidateQueries({ queryKey: ['my-profile'] })
    },
    onError: (error: Error) => {
      toast.error('Kunde inte spara: ' + error.message)
    }
  })

  // Email change handler
  const handleEmailChange = async () => {
    if (!newEmail || newEmail === profile?.email) {
      toast.error('Ange en ny e-postadress')
      return
    }

    setEmailLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ email: newEmail })
      if (error) throw error

      toast.success('En verifieringslänk har skickats till din nya e-postadress')
      setShowEmailChange(false)
      setNewEmail('')
    } catch (error: any) {
      toast.error('Kunde inte ändra e-post: ' + error.message)
    } finally {
      setEmailLoading(false)
    }
  }

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }))
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-nature-900 dark:text-nature-100">
          Kunde inte ladda profil
        </h2>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
            style={{ backgroundColor: profile.color || '#10B981' }}
          >
            {profile.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">
              {profile.name}
            </h1>
            <p className="text-sm text-nature-500 dark:text-nature-400">
              Min profil
            </p>
          </div>
        </div>

        {isEditing ? (
          <div className="flex gap-2">
            <button
              onClick={() => {
                setFormData(originalData!)
                setIsEditing(false)
              }}
              className="btn-secondary"
            >
              Avbryt
            </button>
            <button
              onClick={() => saveMutation.mutate(formData)}
              disabled={!hasChanges || saveMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Spara
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="btn-primary"
          >
            Redigera
          </button>
        )}
      </div>

      {/* Profile Info Section */}
      <div className="card">
        <button
          onClick={() => toggleSection('profile')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-nature-900 dark:text-nature-100">
              Profilinformation
            </h2>
          </div>
          {sectionsOpen.profile ? (
            <ChevronUp className="h-5 w-5 text-nature-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-nature-400" />
          )}
        </button>

        {sectionsOpen.profile && (
          <div className="px-4 pb-4 space-y-4">
            {/* Name (read-only) */}
            <div>
              <label className="form-label">Namn</label>
              <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                {profile.name}
              </div>
              <p className="text-xs text-nature-500 mt-1">
                Kontakta admin för att ändra namn
              </p>
            </div>

            {/* Email */}
            <div>
              <label className="form-label flex items-center gap-2">
                <Mail className="h-4 w-4" />
                E-postadress
              </label>
              <div className="flex gap-2">
                <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400 flex-1">
                  {profile.email}
                </div>
                <button
                  onClick={() => setShowEmailChange(!showEmailChange)}
                  className="btn-secondary text-sm"
                >
                  Ändra
                </button>
              </div>
            </div>

            {/* Email change form */}
            {showEmailChange && (
              <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4 space-y-3">
                <label className="form-label text-sm">Ny e-postadress</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="ny@email.se"
                  className="input-field"
                />
                <p className="text-xs text-nature-500">
                  En verifieringslänk skickas till den nya adressen
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleEmailChange}
                    disabled={emailLoading}
                    className="btn-primary text-sm flex items-center gap-2"
                  >
                    {emailLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    Skicka verifiering
                  </button>
                  <button
                    onClick={() => {
                      setShowEmailChange(false)
                      setNewEmail('')
                    }}
                    className="btn-secondary text-sm"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            {/* Phone */}
            <div>
              <label className="form-label flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Telefon
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="070-123 45 67"
                  className="input-field"
                />
              ) : (
                <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                  {profile.phone || '-'}
                </div>
              )}
            </div>

            {/* Company info */}
            <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4">
              <label className="form-label text-xs uppercase tracking-wider text-nature-500 flex items-center gap-2 mb-3">
                <Building2 className="h-3.5 w-3.5" />
                Företagsuppgifter
              </label>
              <div className="space-y-3">
                <div>
                  <label className="form-label text-sm">Företagsnamn</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Ditt företagsnamn"
                      className="input-field"
                    />
                  ) : (
                    <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                      {profile.company || '-'}
                    </div>
                  )}
                </div>
                <div>
                  <label className="form-label text-sm">Organisationsnummer</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.org_number}
                      onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                      placeholder="XXXXXX-XXXX"
                      className="input-field"
                    />
                  ) : (
                    <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                      {profile.org_number || '-'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Address Section */}
      <div className="card">
        <button
          onClick={() => toggleSection('contact')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-nature-900 dark:text-nature-100">
              Adress
            </h2>
          </div>
          {sectionsOpen.contact ? (
            <ChevronUp className="h-5 w-5 text-nature-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-nature-400" />
          )}
        </button>

        {sectionsOpen.contact && (
          <div className="px-4 pb-4 space-y-4">
            <div>
              <label className="form-label">Gatuadress</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.address_street}
                  onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                  placeholder="Storgatan 1"
                  className="input-field"
                />
              ) : (
                <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                  {profile.address_street || '-'}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Postnummer</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.address_postal_code}
                    onChange={(e) => setFormData({ ...formData, address_postal_code: e.target.value })}
                    placeholder="123 45"
                    className="input-field"
                  />
                ) : (
                  <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                    {profile.address_postal_code || '-'}
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">Ort</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.address_city}
                    onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                    placeholder="Stockholm"
                    className="input-field"
                  />
                ) : (
                  <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                    {profile.address_city || '-'}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="form-label">Land</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.address_country}
                  onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
                  placeholder="Sverige"
                  className="input-field"
                />
              ) : (
                <div className="input-field bg-nature-100 dark:bg-nature-800 text-nature-600 dark:text-nature-400">
                  {profile.address_country || 'Sverige'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Availability Section */}
      <div className="card">
        <button
          onClick={() => toggleSection('availability')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-nature-900 dark:text-nature-100">
              Tillgänglighet
            </h2>
            <span className="text-sm text-nature-500">
              ({formData.available_days.filter(Boolean).length} dagar/vecka)
            </span>
          </div>
          {sectionsOpen.availability ? (
            <ChevronUp className="h-5 w-5 text-nature-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-nature-400" />
          )}
        </button>

        {sectionsOpen.availability && (
          <div className="px-4 pb-4">
            <p className="text-sm text-nature-500 dark:text-nature-400 mb-3">
              Vilka dagar kan du normalt jobba?
            </p>
            <div className="flex flex-wrap gap-1.5">
              {DAY_NAMES.map((day, index) => {
                const isAvailable = formData.available_days[index]
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (!isEditing) return
                      const newDays = [...formData.available_days]
                      newDays[index] = !newDays[index]
                      setFormData({ ...formData, available_days: newDays })
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isAvailable
                        ? 'bg-primary-500 text-white shadow-sm hover:bg-primary-600'
                        : 'bg-nature-200 dark:bg-nature-700 text-nature-500 dark:text-nature-400 hover:bg-nature-300 dark:hover:bg-nature-600'
                    } ${!isEditing ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Comments Section - For communication with admin */}
      <div className="card">
        <button
          onClick={() => toggleSection('comments')}
          className="w-full flex items-center justify-between p-4 text-left"
        >
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-nature-900 dark:text-nature-100">
              Meddelanden till admin
            </h2>
          </div>
          {sectionsOpen.comments ? (
            <ChevronUp className="h-5 w-5 text-nature-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-nature-400" />
          )}
        </button>

        {sectionsOpen.comments && (
          <div className="px-4 pb-4">
            <p className="text-sm text-nature-500 dark:text-nature-400 mb-4">
              Skriv meddelanden här för att kommunicera med administratörerna.
            </p>
            <CommentSection
              entityType="subcontractor"
              entityId={profile.id}
            />
          </div>
        )}
      </div>
    </div>
  )
}
